// Exact-dates support for the planner: turning arrival/departure dates and
// times into plan days, and fitting a built plan to those days — museums off
// their closing weekday, the lightest days on a late arrival / early
// departure, and overflow moved out of a short day.
import { TRIP, SPOTS, isClosedOnDate, dayDateFor, rebuildDayOrder, insertionIndexFor, WEEKDAYS, closureReason, ITINERARY_ALL } from "./planner";

// A day is a curated-itinerary day when all its stops belong to one of the
// suggested itinerary days. When the itinerary suggestion is chosen the plan is
// kept EXACTLY as designed: curated days never reorder (Day 3 stays
// Christianshavn, the Vesterbro day keeps Glyptoteket, …) and no stop hops
// between curated days. Closing days are flagged on the card, not auto-fixed.
const isCuratedDay = (items) =>
  items.length > 0 && ITINERARY_ALL.some((g) => items.every((s) => g.includes(s.id)));

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const parseDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
};
export const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
const daysBetween = (a, b) => Math.round((b - a) / 864e5);
// "Mon 13 Jul"
export const fmtDate = (date) => (date ? `${WEEKDAYS[date.getDay()].slice(0, 3)} ${date.getDate()} ${MONTH_ABBR[date.getMonth()]}` : "");
// 14.5 → "14:30"
export const fmtTime = (h) => `${String(Math.floor(h)).padStart(2, "0")}:${h % 1 ? "30" : "00"}`;

export const EVENING_FROM = 17.5; // free only from 17:30 or later → an arrival evening, not a plan day. 16:30–17:00 still leaves time for a Nyhavn stroll, so it stays a (short) Day 1
export const MORNING_BY = 12; // gone by noon → a departure morning, not a plan day
export const MAX_PLAN_DAYS = 12;

// From the visitor's dates + times to what the planner should plan:
// which calendar days are real sightseeing days, which are just an arrival
// evening / departure morning, the month to plan for, and the first-day /
// last-day time limits.
export function deriveTrip({ start, end, arriveFrom, leaveBy }) {
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e) return null;
  if (e < s) return { invalid: "Your departure is before your arrival — check the dates." };
  const nDates = daysBetween(s, e) + 1;
  const arrivalEvening = nDates > 1 && arriveFrom != null && arriveFrom >= EVENING_FROM;
  const departureMorning = nDates > 1 && leaveBy != null && leaveBy <= MORNING_BY;
  let planDays = nDates - (arrivalEvening ? 1 : 0) - (departureMorning ? 1 : 0);
  const planStart = addDays(s, arrivalEvening ? 1 : 0);
  if (planDays < 1) planDays = 1;
  // the month with most plan days wins (a trip straddling two months)
  const counts = {};
  for (let i = 0; i < planDays; i++) {
    const m = addDays(planStart, i).getMonth() + 1;
    counts[m] = (counts[m] || 0) + 1;
  }
  const month = Number(Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]);
  return {
    start: s,
    end: e,
    nDates,
    planStart,
    planDays,
    month,
    firstFrom: arrivalEvening ? null : arriveFrom,
    lastBy: departureMorning ? null : leaveBy,
    arrivalEvening: arrivalEvening ? s : null,
    departureMorning: departureMorning ? e : null,
    tooLong: planDays > MAX_PLAN_DAYS,
  };
}

const LUNCH_H = 1; // a 1h sit-down lunch break (matches planner.js)
// Stops that get LEFT OUT (not moved) when a late arrival makes day 1 too
// short: the Little Mermaid & Kastellet sit far north and pair badly with any
// other day — better skipped than bolted onto day 2.
const DROP_WHEN_LATE = { littlemermaid: "too far north for a late start, and it doesn't pair with the other days" };
// When a curated day runs short, sacrifice stops in a FIXED order that's
// specific to each suggested day. Earlier in the list = dropped first.
//  • Day 1 (harbour): Kastellet → Nyboder → Amalienborg → Marmorkirken → Broens.
//    Nyhavn and the canal tour are NEVER dropped (the day never trims below them).
//  • Day 3 (Christianshavn): everything after Christiania first (Opera, Reffen),
//    then — only if even shorter — the Vor Frelser tower climb.
// Any day without its own list (Day 2 old town, Day 4 Vesterbro, Day 5 coast)
// is trimmed from the END, so the day just finishes at a reasonable time.
const NEVER_DROP = new Set(["nyhavn", "canaltour"]);
const CURATED_DROP_BY_DAY = {
  harbour: ["littlemermaid", "nyboder", "amalienborg", "marmorkirken", "broens"],
  christianshavn: ["opera", "reffen", "oursaviour"],
};
// which suggested day a set of stops is (by matching the itinerary groups)
const ITIN_DAY_TYPE = ["harbour", "oldtown", "christianshavn", "vesterbro", "coast"];
const curatedDayType = (items) => {
  const idx = ITINERARY_ALL.findIndex((g) => items.length > 0 && items.every((s) => g.includes(s.id)));
  return idx >= 0 ? ITIN_DAY_TYPE[idx] : null;
};
const dist2 = (a, b) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
const COPENHAGEN_CENTRE = [55.6802, 12.5795]; // ~Strøget, for "central" fallbacks
// hours a day takes on the clock: packing hours + the lunch break
const clockHours = (d) => d.hours + (d.hours > 2.5 ? LUNCH_H : 0);
// Hours plan day `dayNum` can hold given the arrival / departure times —
// a normal day runs ~10:00–19:30.
export const dayBudget = (dayNum) => {
  let cap = Infinity;
  if (dayNum === 1 && TRIP.firstFrom != null) cap = Math.min(cap, 19.5 - Math.max(10, TRIP.firstFrom));
  if (dayNum === TRIP.planDays && TRIP.lastBy != null) cap = Math.min(cap, TRIP.lastBy - 10);
  return cap;
};
const conflictsOn = (items, dayNum) => {
  const date = dayDateFor(dayNum);
  return date ? items.filter((s) => isClosedOnDate(s, date)) : [];
};
const slotCost = (d, dayNum) => conflictsOn(d.items, dayNum).length * 10 + Math.max(0, clockHours(d) - dayBudget(dayNum)) * 3;

const permutations = (arr) => {
  if (arr.length <= 1) return [arr];
  return arr.flatMap((x, i) => permutations([...arr.slice(0, i), ...arr.slice(i + 1)]).map((p) => [x, ...p]));
};

// Fit a built plan to the trip's dates. Locked days never move or change.
// 1) re-order the free days over the free slots so closing-day conflicts and
//    arrival/departure overflow are minimal (ties keep the original order),
// 2) move any stop still on its closing day to another day that's open,
// 3) move overflow off a short first/last day to the roomiest day.
// Returns the fitted plan plus human-readable notes on what changed.
export function fitPlanToTrip(plan, month) {
  if (!TRIP.planStart || !plan || !plan.length) return { plan, notes: [], dropped: [], added: [], swapOut: [] };
  const notes = [];
  const dropped = []; // left out of the PLAN (for notes) — stays in `selected` so widening the time restores it
  const added = []; // brought in (Day-5 swap picks) — added to `selected`
  const swapOut = []; // genuinely swapped away (the Day-5 day trip) — removed from `selected`
  const n = plan.length;
  // Curated-itinerary days are pinned to their slot like locked days — the
  // chosen itinerary keeps its exact designed order, so no day (Christianshavn,
  // the Vesterbro/Glyptoteket day, …) ever moves. `pinnedCurated` keeps a
  // curated day pinned after the visitor edits it (the late-arrival rescue).
  const pinnedAt = plan.map((d) => !!d.locked || !!d.pinnedCurated || isCuratedDay(d.items));
  const free = plan.map((d, i) => i).filter((i) => !pinnedAt[i]);

  // --- 1) assignment of free days to free slots --------------------------
  let order = plan.map((_, i) => i);
  if (free.length > 1) {
    const evalOrder = (assign) => {
      let c = 0;
      free.forEach((slot, k) => {
        c += slotCost(plan[assign[k]], slot + 1) + Math.abs(assign[k] - slot) * 0.01;
      });
      return c;
    };
    let best = free.slice();
    let bestC = evalOrder(best);
    if (bestC > 0.5) {
      if (free.length <= 7) {
        permutations(free).forEach((p) => {
          const c = evalOrder(p);
          if (c < bestC - 1e-9) {
            bestC = c;
            best = p;
          }
        });
      } else {
        // greedy pairwise swaps for long trips
        let improved = true;
        while (improved) {
          improved = false;
          for (let a = 0; a < best.length; a++)
            for (let b = a + 1; b < best.length; b++) {
              const cand = best.slice();
              [cand[a], cand[b]] = [cand[b], cand[a]];
              const c = evalOrder(cand);
              if (c < bestC - 1e-9) {
                bestC = c;
                best = cand;
                improved = true;
              }
            }
        }
      }
      free.forEach((slot, k) => {
        order[slot] = best[k];
      });
      if (best.some((idx, k) => idx !== free[k])) notes.push("Days re-ordered so nothing lands on its closing day and your lighter day sits on the short travel day.");
    }
  }
  let days = order.map((idx, slot) => (idx === slot ? plan[idx] : rebuildDayOrder(plan[idx].items, { ...plan[idx], day: slot + 1 }, month)));

  const setDay = (i, items) => {
    days[i] = rebuildDayOrder(items, days[i], month);
  };
  const roomFor = (i, s) => !days[i].locked && days[i].hours + s.dur <= Math.min(9.5, dayBudget(i + 1) - LUNCH_H);
  const moveTo = (fromI, s, targetI) => {
    setDay(fromI, days[fromI].items.filter((x) => x.id !== s.id));
    const items = days[targetI].items.slice();
    items.splice(insertionIndexFor(items, s), 0, s);
    setDay(targetI, items);
  };

  // --- 2) stops still on their closing day → another open day -------------
  for (let i = 0; i < n; i++) {
    if (days[i].locked) continue;
    // a curated day keeps its designed stops: a curated stop that lands on its
    // closing day is left in place and flagged (the day card shows it closed),
    // never shuffled onto another curated day and breaking the itinerary.
    const fromCurated = isCuratedDay(days[i].items);
    conflictsOn(days[i].items, i + 1).forEach((s) => {
      const cands = days
        .map((d, j) => j)
        .filter((j) => j !== i && !isClosedOnDate(s, dayDateFor(j + 1)) && roomFor(j, s))
        .filter((j) => !(fromCurated && isCuratedDay(days[j].items)))
        .sort((a, b) => {
          const same = (j) => (days[j].items.some((x) => x.cluster === s.cluster) ? 0 : 1);
          return same(a) - same(b) || days[a].hours - days[b].hours;
        });
      if (cands.length) {
        moveTo(i, s, cands[0]);
        notes.push(`${s.name} moved to ${fmtDate(dayDateFor(cands[0] + 1))} — ${closureReason(s, dayDateFor(i + 1))} on ${fmtDate(dayDateFor(i + 1))}.`);
      } else if (fromCurated) {
        notes.push(`${s.name} is ${closureReason(s, dayDateFor(i + 1))} on ${fmtDate(dayDateFor(i + 1))} — it stays on this day (its own area); consider swapping days or skipping it.`);
      }
    });
  }

  // --- 3) overflow off a short arrival / departure day ---------------------
  [0, n - 1].forEach((i) => {
    if (i < 0 || days[i].locked || !Number.isFinite(dayBudget(i + 1))) return;

    // Day 5 (a coast / day-trip day) on a short day: a day trip eats a whole
    // day and can't be squeezed in. Swap it for a shorter plan close to the
    // hotel — or, with no hotel, a couple of central spots not already used.
    const isDaytripDay = days[i].items.length > 0 && days[i].items.every((s) => s.cluster === "daytrip");
    if (isDaytripDay && clockHours(days[i]) > dayBudget(i + 1)) {
      const removed = days[i].items.slice();
      const budget = Math.min(9.5, dayBudget(i + 1)) - (dayBudget(i + 1) > 2.5 ? LUNCH_H : 0);
      const anchor = TRIP.hotel?.coords || COPENHAGEN_CENTRE;
      const centralOnly = !TRIP.hotel;
      const inPlan = new Set(days.flatMap((d, j) => (j === i ? [] : d.items.map((s) => s.id))));
      let pool = SPOTS.filter(
        (s) =>
          !inPlan.has(s.id) &&
          s.cluster !== "daytrip" &&
          s.cluster !== "partners" &&
          !s.event &&
          !s.hidePin &&
          !isClosedOnDate(s, dayDateFor(i + 1))
      );
      if (centralOnly) pool = pool.filter((s) => s.cluster === "indre-by");
      pool.sort((a, b) => dist2(a.coords, anchor) - dist2(b.coords, anchor));
      const picks = [];
      let used = 0;
      for (const s of pool) {
        if (used + s.dur + 0.3 > budget) continue;
        picks.push(s);
        used += s.dur + 0.3;
        if (picks.length >= 4) break;
      }
      removed.forEach((s) => { dropped.push(s.id); swapOut.push(s.id); });
      picks.forEach((s) => added.push(s.id));
      setDay(i, picks);
      const shortBecause = TRIP.lastBy != null ? `you leave by ${fmtTime(TRIP.lastBy)}` : "your short day";
      if (picks.length) {
        const where = TRIP.hotel ? "near your hotel" : "in the city centre";
        notes.push(`${removed.map((s) => s.name).join(" & ")} needs a full day — with ${shortBecause}, we swapped in a shorter plan ${where}: ${picks.map((s) => s.name).join(", ")}.`);
      } else {
        notes.push(`${removed.map((s) => s.name).join(" & ")} needs a full day and won't fit with ${shortBecause}.`);
      }
      return; // this day is handled; skip the generic trim below
    }

    // late arrival: the far-flung stops are skipped outright first
    if (i === 0 && TRIP.firstFrom != null) {
      days[i].items
        .filter((s) => DROP_WHEN_LATE[s.id] && clockHours(days[i]) > dayBudget(i + 1))
        .forEach((s) => {
          setDay(i, days[i].items.filter((x) => x.id !== s.id));
          dropped.push(s.id);
          notes.push(`${s.name} left out — ${DROP_WHEN_LATE[s.id]} (you're free from ${fmtTime(TRIP.firstFrom)}).`);
        });
    }
    let guard = 12;
    while (clockHours(days[i]) > dayBudget(i + 1) && days[i].items.length > 1 && guard-- > 0) {
      const items = days[i].items;
      const curatedDay = isCuratedDay(items);
      // Which stop leaves next. A curated day follows its own fixed order:
      //   • Day 1 (harbour): Kastellet → Nyboder → Amalienborg → Marmorkirken →
      //     Broens; never Nyhavn / the canal tour.
      //   • Day 3 (Christianshavn): Opera → Reffen (everything after Christiania)
      //     → then the tower climb.
      //   • Day 2 / Day 4 / anything else: trim from the END so the day just
      //     finishes at a reasonable time.
      // A custom day drops its longest non-pinned stop.
      let s;
      if (curatedDay) {
        const dayType = curatedDayType(items);
        const droppable = items.filter((x) => !x.pinned && !NEVER_DROP.has(x.id));
        const order = CURATED_DROP_BY_DAY[dayType];
        const listed = order
          ? droppable.filter((x) => order.includes(x.id)).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
          : [];
        if (listed.length) {
          s = listed[0];
        } else if (dayType === "harbour") {
          break; // Day 1 never trims below Nyhavn + the canal tour
        } else {
          s = droppable[droppable.length - 1]; // trim the latest stop
        }
      } else {
        s = items.filter((x) => !x.pinned).sort((a, b) => b.dur - a.dur)[0];
      }
      if (!s) break;
      const cands = days
        .map((d, j) => j)
        .filter((j) => j !== i && roomFor(j, s) && !isClosedOnDate(s, dayDateFor(j + 1)))
        .sort((a, b) => {
          const same = (j) => (days[j].items.some((x) => x.cluster === s.cluster) ? 0 : 1);
          return same(a) - same(b) || days[a].hours - days[b].hours;
        });
      // A curated day never dumps a stop onto ANOTHER curated (itinerary) day —
      // that breaks the designed plan. It may only lend a stop to a NON-curated
      // same-area day; otherwise the stop is simply left out.
      const target = curatedDay
        ? cands.find((j) => !isCuratedDay(days[j].items) && days[j].items.some((x) => x.cluster === s.cluster))
        : cands[0];
      if (target == null) {
        if (curatedDay) {
          setDay(i, items.filter((x) => x.id !== s.id));
          dropped.push(s.id);
          notes.push(
            i === 0
              ? `${s.name} left out — no time for it with a ${fmtTime(TRIP.firstFrom)} start.`
              : `${s.name} left out — you leave by ${fmtTime(TRIP.lastBy)} on your last day.`
          );
          continue;
        }
        break;
      }
      moveTo(i, s, target);
      notes.push(
        i === 0
          ? `${s.name} moved to ${fmtDate(dayDateFor(target + 1))} — you're only free from ${fmtTime(TRIP.firstFrom)} on arrival day.`
          : `${s.name} moved to ${fmtDate(dayDateFor(target + 1))} — you leave by ${fmtTime(TRIP.lastBy)} on your last day.`
      );
    }
  });

  return { plan: days.map((d, i) => (d.day === i + 1 ? d : { ...d, day: i + 1 })), notes, dropped, added, swapOut };
}
